'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { trackEvent } from '@/components/GoogleAnalytics';
import { PUBLIC_SEARCH_AGE_GROUPS, publicSearchPath } from '@/utils/publicSearch';

const AGE_OPTIONS = PUBLIC_SEARCH_AGE_GROUPS;

type LookingFor = 'bride' | 'groom';

type HomeMobileSearchProps = {
  trackingSource?: string;
};

export default function HomeMobileSearch({
  trackingSource = 'homepage_mobile_search',
}: HomeMobileSearchProps) {
  const router = useRouter();
  const [lookingFor, setLookingFor] = useState<LookingFor>('bride');
  const [ageIndex, setAgeIndex] = useState(1);
  const [ageOpen, setAgeOpen] = useState(false);
  const ageRef = useRef<HTMLDivElement>(null);
  const ageListId = useId();
  const age = AGE_OPTIONS[ageIndex];

  useEffect(() => {
    if (!ageOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ageRef.current?.contains(event.target as Node)) {
        setAgeOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAgeOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ageOpen]);

  const handleSearch = () => {
    const gender = lookingFor === 'bride' ? 'female' : 'male';
    trackEvent('click_mobile_search', trackingSource, `${lookingFor} ${age.label}`);
    router.push(publicSearchPath(gender, age.ageGroup));
  };

  return (
    <section className="bg-gradient-to-b from-[#ffeff7] via-[#f7effa] to-[#f1f0ff] px-5 py-16 font-sans md:hidden">
      <div className="mx-auto flex w-full max-w-[360px] flex-col items-center text-center">
        <p className="text-[14px] font-bold uppercase tracking-[1.4px] text-black">Quick Search</p>
        <h2 className="mt-2 text-[24px] font-bold leading-tight text-black">Search Your Life Partner</h2>

        <div
          className="mt-5 flex w-full items-center rounded-full bg-[#fce8f1] p-1.5"
          role="radiogroup"
          aria-label="Looking for"
        >
          <button
            type="button"
            role="radio"
            aria-checked={lookingFor === 'bride'}
            onClick={() => setLookingFor('bride')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-3 text-[15px] font-bold text-[#232323] ${
              lookingFor === 'bride' ? 'bg-white shadow-sm' : ''
            }`}
          >
            {lookingFor === 'bride' && (
              <span className="flex size-5 items-center justify-center rounded-full border-2 border-primary-600" aria-hidden>
                <span className="size-2.5 rounded-full bg-primary-600" />
              </span>
            )}
            वधू (Bride)
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={lookingFor === 'groom'}
            onClick={() => setLookingFor('groom')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-3 text-[15px] font-bold text-[#232323] ${
              lookingFor === 'groom' ? 'bg-white shadow-sm' : ''
            }`}
          >
            {lookingFor === 'groom' && (
              <span className="flex size-5 items-center justify-center rounded-full border-2 border-primary-600" aria-hidden>
                <span className="size-2.5 rounded-full bg-primary-600" />
              </span>
            )}
            वर (Groom)
          </button>
        </div>

        <div className="relative mt-3 w-full" ref={ageRef}>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={ageOpen}
            aria-controls={ageListId}
            onClick={() => setAgeOpen((open) => !open)}
            className="flex w-full items-center justify-between rounded-full border border-[#dfdfdf] bg-white px-5 py-3.5 text-[15px] font-medium text-[#232323]"
          >
            {age.label}
            <ChevronDown
              size={20}
              aria-hidden
              className={`shrink-0 text-[#232323] transition-transform ${ageOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {ageOpen && (
            <ul
              id={ageListId}
              role="listbox"
              aria-label="Age range"
              className="absolute top-full left-0 z-20 mt-2 w-full overflow-hidden rounded-2xl border border-[#dfdfdf] bg-white py-1 shadow-lg"
            >
              {AGE_OPTIONS.map((option, index) => (
                <li key={option.label} role="option" aria-selected={index === ageIndex}>
                  <button
                    type="button"
                    className={`w-full px-4 py-2.5 text-left text-sm ${
                      index === ageIndex
                        ? 'bg-[#fce8f1] font-semibold text-[#232323]'
                        : 'text-[#232323] hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setAgeIndex(index);
                      setAgeOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          type="button"
          onClick={handleSearch}
          className="mt-3 w-full rounded-full bg-primary-600 py-3.5 text-[16px] font-bold text-white transition-colors hover:bg-primary-700"
        >
          Search Now
        </button>
      </div>
    </section>
  );
}
